import type { ConversationRun, ConversationToolCall } from "./types.js";

const ACTIVE_RUN_STATES = new Set<ConversationRun["status"]>([
  "queued",
  "provisioning",
  "running",
  "pausing",
  "resuming",
  "cancelling",
]);

export type ToolActivityLifecycle = "working" | "recovering" | "completed" | "failed" | "cancelled" | "interrupted";

export interface PresentedToolCall {
  call: ConversationToolCall;
  summary: string;
}

export interface ToolActivityView {
  lifecycle: ToolActivityLifecycle;
  calls: PresentedToolCall[];
  latest: PresentedToolCall;
  countLabel: string;
  statusLabel: string;
  detailLabel: string;
}

interface ActivityMeaning {
  running: string;
  succeeded: string;
  failed: string;
  cancelled: string;
}

/**
 * Converts a retained tool burst into the two user-facing facts the collapsed
 * card needs: what is happening now and how many exact calls sit behind it.
 * Raw input and output stay attached to each call for the technical layer.
 */
export function buildToolActivityView(
  calls: readonly ConversationToolCall[],
  active: boolean,
): ToolActivityView {
  if (calls.length === 0) {
    throw new Error("Tool activity needs at least one call.");
  }

  const presented = calls.map((call) => ({
    call,
    summary: toolCallSummary(call),
  }));
  const latest = presented.at(-1)!;
  const running = calls.filter(({ status }) => status === "running").length;
  const finished = calls.length - running;
  const lifecycle = activityLifecycle(calls, active);

  return {
    lifecycle,
    calls: presented,
    latest,
    // People follow steps, not shell commands or tool calls.
    countLabel: `${calls.length} ${plural(calls.length, "step", "steps")}`,
    statusLabel: lifecycleLabel(lifecycle),
    detailLabel: [
      finished > 0
        ? `${finished} ${plural(finished, "finished", "finished")}`
        : null,
      running > 0
        ? `${running} ${active ? "running" : "without a final result"}`
        : null,
    ]
      .filter((value): value is string => value !== null)
      .join(" · "),
  };
}

/** Only unfinished calls in their own active run can still be working. */
export function toolActivityIsLive(
  calls: readonly ConversationToolCall[],
  runs: readonly ConversationRun[],
): boolean {
  const runById = new Map(runs.map((run) => [run.id, run]));
  return calls.some((call) => {
    const run = runById.get(call.runId);
    return call.status === "running" && run !== undefined && ACTIVE_RUN_STATES.has(run.status);
  });
}

export function formatToolDuration(durationMs: number): string {
  if (durationMs < 1_000) return `${durationMs}ms`;
  if (durationMs < 60_000) {
    const seconds = durationMs / 1_000;
    return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  }
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.round((durationMs % 60_000) / 1_000);
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}

export function readableToolName(tool: string): string {
  const words = tool
    .replace(/^codex\./, "")
    .replace(/^mcp[_:.]+/i, "")
    .replace(/[._:-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return words
    ? words.replace(/\b\w/g, (character) => character.toUpperCase())
    : "Agent tool";
}

/** Preserve terminal failures and stops instead of implying successful work. */
function activityLifecycle(
  calls: readonly ConversationToolCall[],
  active: boolean,
): ToolActivityLifecycle {
  const latest = calls.at(-1)!;
  if (calls.some((call) => call.status === "running")) {
    if (!active) return "interrupted";
    return calls.some((call) => call.status === "failed") ? "recovering" : "working";
  }
  if (latest.status === "failed") return "failed";
  if (latest.status === "cancelled") return "cancelled";
  return "completed";
}

function lifecycleLabel(lifecycle: ToolActivityLifecycle): string {
  if (lifecycle === "working") return "Working";
  if (lifecycle === "recovering") return "Working";
  if (lifecycle === "failed") return "Failed";
  if (lifecycle === "cancelled") return "Stopped";
  if (lifecycle === "interrupted") return "Interrupted";
  return "Finished";
}

function toolCallSummary(call: ConversationToolCall): string {
  const starter = appStarterMeaning(call);
  if (starter) return starter[call.status];
  const meaning =
    call.kind === "command"
      ? commandMeaning(call.input ?? call.summary)
      : toolMeaning(call.tool);
  return meaning[call.status];
}

function appStarterMeaning(
  call: ConversationToolCall,
): ActivityMeaning | null {
  const tool = call.tool
    .toLocaleLowerCase()
    .replace(/[._:-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (tool !== "materialize app starter") return null;

  const name = appStarterName(call);
  const selected = name ? `the ${name} starter` : "an app starter";
  return meaning(
    `Selecting ${selected}`,
    `Selected ${selected}`,
    "The selected app starter needs another try",
    "Stopped selecting the app starter",
  );
}

function appStarterName(call: ConversationToolCall): string | null {
  for (const source of [call.output, call.input]) {
    if (!source) continue;
    try {
      const parsed = JSON.parse(source) as Record<string, unknown>;
      if (typeof parsed.starterName === "string" && parsed.starterName.trim()) {
        return parsed.starterName.trim();
      }
      if (typeof parsed.starterId === "string" && parsed.starterId.trim()) {
        return parsed.starterId
          .trim()
          .split("-")
          .filter(Boolean)
          .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
          .join(" ");
      }
    } catch {
      // Retained tool text is best-effort evidence, so malformed JSON falls
      // back to the generic starter label instead of affecting the transcript.
    }
  }
  return null;
}

function commandMeaning(value: string): ActivityMeaning {
  const command = value.toLocaleLowerCase().replace(/\s+/g, " ").trim();

  if (
    packageScript(command, "format|fmt") ||
    /\b(?:prettier|dprint|gofmt|rustfmt)\b/.test(command) ||
    /\bbiome\s+format\b/.test(command)
  ) {
    return meaning(
      "Formatting the app’s files",
      "Formatted the app’s files",
      "The formatting step needs another try",
      "Stopped formatting the app’s files",
    );
  }

  if (
    packageScript(command, "test(?::[a-z0-9_-]+)?|e2e") ||
    /\b(?:vitest|jest|pytest|rspec)\b/.test(command) ||
    /\b(?:playwright\s+test|cypress\s+run|go\s+test|cargo\s+test)\b/.test(
      command,
    )
  ) {
    return meaning(
      "Checking that the app works",
      "Checked that the app works",
      "A check found something to fix",
      "Stopped checking the app",
    );
  }

  if (
    packageScript(command, "lint|typecheck|type-check|check|verify|validate") ||
    /\b(?:eslint|stylelint|tsc(?:\s|$)|biome\s+check)\b/.test(command) ||
    /\bopencloud\s+(?:app\s+)?validate\b/.test(command)
  ) {
    return meaning(
      "Checking the app for issues",
      "Checked the app for issues",
      "A check found something to fix",
      "Stopped checking the app",
    );
  }

  if (
    packageScript(command, "build") ||
    /\b(?:vite|next|nuxt|astro)\s+build\b/.test(command) ||
    /\b(?:go|cargo)\s+build\b/.test(command) ||
    /\btsc\s+(?:-[a-z]*b\b|--build\b)/.test(command)
  ) {
    return meaning(
      "Building the updated app",
      "Built the updated app",
      "The app build found something to fix",
      "Stopped building the app",
    );
  }

  if (
    /\b(?:npm\s+(?:install|i|ci)|pnpm\s+(?:install|i)|yarn\s+install|bun\s+install|pip\s+install|bundle\s+install)\b/.test(
      command,
    )
  ) {
    return meaning(
      "Preparing the app’s building blocks",
      "Prepared the app’s building blocks",
      "The app’s building blocks need another try",
      "Stopped preparing the app’s building blocks",
    );
  }

  if (
    /\b(?:prisma|drizzle|knex|sequelize|typeorm)\b.*\b(?:migrate|push)\b/.test(
      command,
    ) ||
    /\b(?:migrate|migration)\b/.test(command)
  ) {
    return meaning(
      "Updating the app’s data structure",
      "Updated the app’s data structure",
      "The data update needs another look",
      "Stopped updating the app’s data structure",
    );
  }

  if (
    packageScript(command, "dev|start|preview|serve") ||
    /\b(?:vite|next|nuxt|astro)\s+(?:dev|preview|start)\b/.test(command)
  ) {
    return meaning(
      "Starting an app preview",
      "Started an app preview",
      "The app preview needs another try",
      "Stopped starting the app preview",
    );
  }

  if (
    packageScript(command, "deploy|publish|release") ||
    /\bopencloud\s+(?:app\s+)?(?:deploy|publish|release)\b/.test(command)
  ) {
    return meaning(
      "Preparing the app for release",
      "Prepared the app for release",
      "The release step needs another look",
      "Stopped preparing the app for release",
    );
  }

  if (/\bgit\s+(?:apply|am)\b/.test(command)) {
    return meaning(
      "Updating the app’s files",
      "Updated the app’s files",
      "An app file update needs another look",
      "Stopped updating the app’s files",
    );
  }

  if (/\bgit\s+(?:diff|status)\b/.test(command)) {
    return meaning(
      "Reviewing the changes",
      "Reviewed the changes",
      "The changes could not be reviewed",
      "Stopped reviewing the changes",
    );
  }

  // Scan command segments once. A repeated prefix regex can backtrack
  // exponentially on retained command chains and freeze the transcript.
  if (
    command.split(/[;&|]+/).some((segment) => /^pwd(?:\s|$)/.test(segment.trimStart()))
  ) {
    return meaning(
      "Finding the app workspace",
      "Found the app workspace",
      "The app workspace still needs to be located",
      "Stopped finding the app workspace",
    );
  }

  if (/(?:^|[;&|]\s*)(?:rg\s+--files|find\s|fd\s|ls(?:\s|$))/.test(command)) {
    return meaning(
      "Reviewing the app’s files",
      "Reviewed the app’s files",
      "The app’s files could not be reviewed",
      "Stopped reviewing the app’s files",
    );
  }

  if (/(?:^|[;&|]\s*)(?:rg|grep)(?:\s|$)/.test(command)) {
    return meaning(
      "Searching the app’s files",
      "Searched the app’s files",
      "The file search needs another look",
      "Stopped searching the app’s files",
    );
  }

  if (/(?:^|[;&|]\s*)(?:cat|head|tail|less|sed\s+-n)(?:\s|$)/.test(command)) {
    return meaning(
      "Reviewing an app file",
      "Reviewed an app file",
      "An app file could not be reviewed",
      "Stopped reviewing an app file",
    );
  }

  if (/(?:^|[;&|]\s*)(?:mkdir|touch|cp|mv)(?:\s|$)/.test(command)) {
    return meaning(
      "Organizing the app’s files",
      "Organized the app’s files",
      "An app file change needs another look",
      "Stopped organizing the app’s files",
    );
  }

  if (/\bgit\s+(?:log|show|branch|rev-parse)\b/.test(command)) {
    return meaning(
      "Reviewing the app’s change history",
      "Reviewed the app’s change history",
      "The change history could not be reviewed",
      "Stopped reviewing the app’s change history",
    );
  }

  if (/\b(?:curl|wget)\b/.test(command)) {
    return meaning(
      "Checking an app service",
      "Checked an app service",
      "An app service check found something to fix",
      "Stopped checking an app service",
    );
  }

  if (/\b(?:docker|podman)(?:\s+compose)?\b/.test(command)) {
    return meaning(
      "Preparing the app workspace",
      "Prepared the app workspace",
      "The app workspace needs another look",
      "Stopped preparing the app workspace",
    );
  }

  return meaning(
    "Working on your app",
    "Finished a step",
    "A step needs another look",
    "Stopped a step",
  );
}

function toolMeaning(tool: string): ActivityMeaning {
  const value = tool
    .toLocaleLowerCase()
    .replace(/[._:-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (value === "opencloud publish and verify") {
    return meaning(
      "Publishing and checking the live app",
      "Published and checked the live app",
      "The live check found something to fix",
      "Stopped publishing and checking the live app",
    );
  }
  if (value === "opencloud production verify") {
    return meaning(
      "Checking the live app",
      "Checked the live app",
      "The live check found something to fix",
      "Stopped checking the live app",
    );
  }
  if (/\b(?:file change|apply patch|write|edit|patch)\b/.test(value)) {
    return meaning(
      "Updating the app’s files",
      "Updated the app’s files",
      "An app file update needs another look",
      "Stopped updating the app’s files",
    );
  }
  if (/\b(?:read|view)(?: file)?\b/.test(value)) {
    return meaning(
      "Reviewing an app file",
      "Reviewed an app file",
      "An app file could not be reviewed",
      "Stopped reviewing an app file",
    );
  }
  if (/\b(?:web search|web fetch|fetch url)\b/.test(value)) {
    return meaning(
      "Reviewing a web resource",
      "Reviewed a web resource",
      "The web resource could not be reviewed",
      "Stopped reviewing a web resource",
    );
  }
  if (/\b(?:grep|glob|find|search|query)\b/.test(value)) {
    return meaning(
      "Searching for useful information",
      "Found useful information",
      "The search needs another look",
      "Stopped the search",
    );
  }
  if (/\b(?:browser|screenshot|playwright|cypress)\b/.test(value)) {
    return meaning(
      "Checking the app in a browser",
      "Checked the app in a browser",
      "The browser check found something to fix",
      "Stopped checking the app in a browser",
    );
  }
  if (/\b(?:skill|guidance|instructions?)\b/.test(value)) {
    return meaning(
      "Reviewing app-building guidance",
      "Reviewed app-building guidance",
      "The app-building guidance needs another look",
      "Stopped reviewing app-building guidance",
    );
  }
  if (/\b(?:task|collaboration|agent)\b/.test(value)) {
    return meaning(
      "Coordinating specialist work",
      "Finished specialist work",
      "Specialist work needs another look",
      "Stopped specialist work",
    );
  }
  if (/\b(?:todo(?:write)?|plan)\b/.test(value)) {
    return meaning(
      "Updating the work plan",
      "Updated the work plan",
      "The work plan needs another look",
      "Stopped updating the work plan",
    );
  }
  if (/\b(?:image|asset)\b/.test(value)) {
    return meaning(
      "Creating an app image",
      "Created an app image",
      "The app image needs another look",
      "Stopped creating an app image",
    );
  }
  if (/\b(?:deploy|publish|release|promote)\b/.test(value)) {
    return meaning(
      "Preparing the app for release",
      "Prepared the app for release",
      "The release step needs another look",
      "Stopped preparing the app for release",
    );
  }
  if (/\b(?:test|check|verify|validate)\b/.test(value)) {
    return meaning(
      "Checking that the app works",
      "Checked that the app works",
      "A check found something to fix",
      "Stopped checking the app",
    );
  }
  if (/\b(?:preview|development)\b/.test(value)) {
    return meaning(
      "Preparing a safe app preview",
      "Prepared a safe app preview",
      "The app preview needs another look",
      "Stopped preparing the app preview",
    );
  }

  return meaning(
    "Running an app-building step",
    "Finished an app-building step",
    "An app-building step returned an error",
    "Stopped an app-building step",
  );
}

function packageScript(command: string, scripts: string): boolean {
  return new RegExp(
    `\\b(?:npm|pnpm|yarn|bun)(?:\\s+run)?\\s+(?:${scripts})(?::[a-z0-9_-]+)?(?:\\s|$)`,
  ).test(command);
}

function meaning(
  running: string,
  succeeded: string,
  failed: string,
  cancelled: string,
): ActivityMeaning {
  return { running, succeeded, failed, cancelled };
}

function plural(count: number, singular: string, multiple: string): string {
  return count === 1 ? singular : multiple;
}
